import { UntilDestroy } from "@ngneat/until-destroy";
import { Injectable, signal, WritableSignal } from "@angular/core";
import { RateCardService } from "src/app/services/http/rate-card.service";
import { catchError, finalize, map } from "rxjs/operators";
import { SnackBarService } from "src/app/services/snackbar/snackBar.service";
import { of } from "rxjs";
import { TradingFacades } from "../order/facades/trading.facades";
import { CouponFrequency } from "../../models/termDeposits/coupon-frequency.enum";
import { InterestCalculationMethod } from "../../models/termDeposits/interest-calculation-method";


@UntilDestroy()
@Injectable()
export class TermDepositFacade {
    public loadingTermDeposits = signal(false);

    public termDepositRateCards: WritableSignal<IRateCardData[]> = signal(null);

    public constructor(
        private rateCardService: RateCardService,
        private snackBarService: SnackBarService,
        private tradeFacades: TradingFacades,) {
    }

    public loadRateCards(): void {
        this.loadingTermDeposits.set(true);
        this.tradeFacades.portfolioId$.subscribe((portfolioId) => {
            if(!!portfolioId) {
                this.rateCardService
                    .getRateCards(
                        portfolioId
                    )
                    .pipe(
                        map((rateCardData) => this.termDepositRateCards.set(rateCardData)),
                        catchError(() => {
                            this.snackBarService.showWarningSnackBar(
                                'Failed to load term deposit rate cards',
                            );

                            return of(null);
                        }),
                        finalize(() => this.loadingTermDeposits.set(false)),
                    )
                    .subscribe();
            }
        });
    }

}

export interface IRateCardData extends 
    IInterestCalculationMethod, 
    IRateCardInterestRate, 
    IRateCardMarginRate, 
    IRateCardCanPayOnMaturity,
    IRateCardInterestPaymentFrequency{
    id?: number;
    bankId?: number;
    bankName?: string;
    productName?: string;
    productDescription?: string;
    effectiveDate?: Date;
    termsAndConditionsDownloadToken?: string;
    termsAndConditionsDocumentId?: number;
    minimumOrderQuantity: number;
    wholeUnitsOnly: boolean;
}

export type RateCardDataKey = keyof IRateCardData;

export type TermDepositTermRateCardDataKey = {
    OneMonth: RateCardDataKey,
    ThreeMonth: RateCardDataKey,
    SixMonth: RateCardDataKey,
    NineMonth: RateCardDataKey,
    TwelveMonth: RateCardDataKey,
    EighteenMonth: RateCardDataKey,
    TwoYear: RateCardDataKey,
    ThreeYear: RateCardDataKey,
    FourYear: RateCardDataKey,
    FiveYear: RateCardDataKey,
}

interface IRateCardInterestRate {
    // Interest Rates
    oneMonthRate?: number;
    threeMonthRate?: number;
    sixMonthRate?: number;
    nineMonthRate?: number;
    twelveMonthRate?: number;
    eighteenMonthRate?: number;
    twoYearRate?: number;
    threeYearRate?: number;
    fourYearRate?: number;
    fiveYearRate?: number;
}

export const RateCardRateKey
    : Record<keyof TermDepositTermRateCardDataKey, keyof IRateCardInterestRate> = {
    OneMonth: 'oneMonthRate',
    ThreeMonth: 'threeMonthRate',
    SixMonth: 'sixMonthRate',
    NineMonth: 'nineMonthRate',
    TwelveMonth: 'twelveMonthRate',
    EighteenMonth: 'eighteenMonthRate',
    TwoYear: 'twoYearRate',
    ThreeYear: 'threeYearRate',
    FourYear: 'fourYearRate',
    FiveYear: 'fiveYearRate',
};

interface IInterestCalculationMethod{
    oneMonthInterestCalculationMethod: InterestCalculationMethod;
    threeMonthInterestCalculationMethod: InterestCalculationMethod;
    sixMonthInterestCalculationMethod: InterestCalculationMethod;
    nineMonthInterestCalculationMethod: InterestCalculationMethod;
    twelveMonthInterestCalculationMethod: InterestCalculationMethod;
    eighteenMonthInterestCalculationMethod: InterestCalculationMethod;
    twoYearInterestCalculationMethod: InterestCalculationMethod;
    threeYearInterestCalculationMethod: InterestCalculationMethod;
    fourYearInterestCalculationMethod: InterestCalculationMethod;
    fiveYearInterestCalculationMethod: InterestCalculationMethod;
}

export const InterestCalculationMethodKey
    : Record<keyof TermDepositTermRateCardDataKey, keyof IInterestCalculationMethod> = {
    OneMonth: 'oneMonthInterestCalculationMethod',
    ThreeMonth: 'threeMonthInterestCalculationMethod',
    SixMonth: 'sixMonthInterestCalculationMethod',
    NineMonth: 'nineMonthInterestCalculationMethod',
    TwelveMonth: 'twelveMonthInterestCalculationMethod',
    EighteenMonth: 'eighteenMonthInterestCalculationMethod',
    TwoYear: 'twoYearInterestCalculationMethod',
    ThreeYear: 'threeYearInterestCalculationMethod',
    FourYear: 'fourYearInterestCalculationMethod',
    FiveYear: 'fiveYearInterestCalculationMethod',
};

interface IRateCardMarginRate {
    // Margin Rates
    oneMonthMargin?: number;
    threeMonthMargin?: number;
    sixMonthMargin?: number;
    nineMonthMargin?: number;
    twelveMonthMargin?: number;
    eighteenMonthMargin?: number;
    twoYearMargin?: number;
    threeYearMargin?: number;
    fourYearMargin?: number;
    fiveYearMargin?: number;
}

interface IRateCardCanPayOnMaturity {
    // Can pay on maturity
    oneMonthCanPayOnMaturity?: boolean;
    threeMonthCanPayOnMaturity?: boolean;
    sixMonthCanPayOnMaturity?: boolean;
    nineMonthCanPayOnMaturity?: boolean;
    twelveMonthCanPayOnMaturity?: boolean;
    eighteenMonthCanPayOnMaturity?: boolean;
    twoYearCanPayOnMaturity?: boolean;
    threeYearCanPayOnMaturity?: boolean;
    fourYearCanPayOnMaturity?: boolean;
    fiveYearCanPayOnMaturity?: boolean;
}

interface IRateCardInterestPaymentFrequency{
    // Interest Payment Frequency
    oneMonthInterestPaymentFrequency: CouponFrequency;
    threeMonthInterestPaymentFrequency: CouponFrequency;
    sixMonthInterestPaymentFrequency: CouponFrequency;
    nineMonthInterestPaymentFrequency: CouponFrequency;
    twelveMonthInterestPaymentFrequency: CouponFrequency;
    eighteenMonthInterestPaymentFrequency: CouponFrequency;
    twoYearInterestPaymentFrequency: CouponFrequency;
    threeYearInterestPaymentFrequency: CouponFrequency;
    fourYearInterestPaymentFrequency: CouponFrequency;
    fiveYearInterestPaymentFrequency: CouponFrequency;
}

export const TermDepositTermKeyPrefix: {
    [key in keyof TermDepositTermRateCardDataKey]: string;
} = {
    OneMonth: "oneMonth",
    ThreeMonth: "threeMonth",
    SixMonth: "sixMonth",
    NineMonth: "nineMonth",
    TwelveMonth: "twelveMonth",
    EighteenMonth: "eighteenMonth",
    TwoYear: "twoYear",
    ThreeYear: "threeYear",
    FourYear: "fourYear",
    FiveYear: "fiveYear",
};
